import { useCallback, useRef } from 'react';

// Minimum horizontal distance required for a valid swipe
const SWIPE_MIN_X = 45;
// Maximum vertical distance allowed during swipe
const SWIPE_MAX_Y = 45;
// Maximum distance to register a click
const CLICK_MAX = 5;

export default function useSwipeTracking(
  triggerSpinner: (action: string) => void
) {
  const currentPos = useRef({ x: 0, y: 0 });
  const startPos = useRef({ x: 0, y: 0 });

  const handleMove = useCallback(({ x, y }: { x: number; y: number }) => {
    const deltaY = Math.abs(startPos.current.y - currentPos.current.y);

    // Reset swipe start once movement goes out of vertical range
    if (deltaY > SWIPE_MAX_Y) {
      startPos.current = { x: 0, y: 0 };
    }

    currentPos.current = { x, y };
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    startPos.current = { x: e.clientX, y: e.clientY };
  }, []);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      handleMove({ x: e.clientX, y: e.clientY });
    },
    [handleMove]
  );

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    startPos.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY
    };
  }, []);

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!e.touches[0]?.clientX || !e.touches[0]?.clientY) return;
      handleMove({
        x: e.touches[0].clientX,
        y: e.touches[0].clientY
      });
    },
    [handleMove]
  );

  const handleInteractionEnd = useCallback(() => {
    const deltaX = Math.abs(startPos.current.x - currentPos.current.x);
    const deltaY = Math.abs(startPos.current.y - currentPos.current.y);

    const isValidClick = deltaX <= CLICK_MAX && deltaY <= CLICK_MAX;
    const isValidSwipe = deltaX > SWIPE_MIN_X && deltaY < SWIPE_MAX_Y;

    if (isValidClick) {
      triggerSpinner('clicked');

      return;
    }

    if (isValidSwipe) {
      triggerSpinner('swiped');

      return;
    }
  }, [triggerSpinner]);

  return {
    onMouseDown: handleMouseDown,
    onMouseMove: handleMouseMove,
    onMouseUp: handleInteractionEnd,
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleInteractionEnd
  };
}
